"use client";

import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { homeworkDueLabel } from "@/lib/homework-due";
import type { HomeworkTaskDto } from "@/lib/types";

export type HomeworkCreateTarget = {
  subject: string;
  dueDate: string;
  slotOrder: number;
};

export function HomeworkEditDialog({
  task,
  create,
  open,
  onClose,
  onSaved,
}: {
  task: HomeworkTaskDto | null;
  create?: HomeworkCreateTarget | null;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const adding = !task && Boolean(create);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [dueManual, setDueManual] = useState(false);
  const [slotOrder, setSlotOrder] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (task) {
      setSubject(task.subject);
      setBody(task.task);
      setDueDate(task.dueDate);
      setDueManual(task.dueManual);
      setSlotOrder(task.slotOrder);
      return;
    }
    if (create) {
      setSubject(create.subject);
      setBody("");
      setDueDate(create.dueDate);
      setDueManual(true);
      setSlotOrder(create.slotOrder);
    }
  }, [open, task, create?.subject, create?.dueDate, create?.slotOrder]);

  async function save() {
    if (!subject.trim() || !body.trim()) {
      toast.error("Потрібні предмет і завдання.");
      return;
    }
    setSaving(true);
    try {
      if (task) {
        const result = await api<{
          delivered: number;
          wouldNotify: number;
          staffOnly?: boolean;
        }>(`/api/homework/tasks/${encodeURIComponent(task.id)}`, {
          method: "PATCH",
          body: JSON.stringify({
            subject: subject.trim(),
            task: body.trim(),
            dueDate,
            dueManual,
          }),
        });
        if (result.delivered > 0) {
          toast.success(
            result.staffOnly ? `Збережено. Сповіщення staff: ${result.delivered}.` : `Надіслано зміни ${result.delivered} батькам.`,
          );
        } else {
          toast.success("Збережено в щоденнику.");
        }
      } else {
        const result = await api<{
          delivered: number;
          wouldNotify: number;
          staffOnly?: boolean;
        }>("/api/homework/tasks", {
          method: "POST",
          body: JSON.stringify({
            rows: [
              {
                subject: subject.trim(),
                task: body.trim(),
                dueDate,
                dueManual: true,
                slotOrder,
              },
            ],
          }),
        });
        if (result.delivered > 0) {
          toast.success(
            result.staffOnly ? `Збережено. Сповіщення staff: ${result.delivered}.` : `Надіслано ${result.delivered} батькам.`,
          );
        } else {
          toast.success("Збережено в щоденнику.");
        }
      }
      onSaved();
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Не вдалося зберегти.");
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!task) return;
    if (!window.confirm("Прибрати це завдання з щоденника?")) return;
    setDeleting(true);
    try {
      await api(`/api/homework/tasks/${encodeURIComponent(task.id)}`, { method: "DELETE" });
      toast.success("Видалено.");
      onSaved();
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Не вдалося видалити.");
    } finally {
      setDeleting(false);
    }
  }

  async function onSubjectBlur() {
    if (dueManual || !subject.trim()) return;
    try {
      const next = await api<{ dueDate: string | null; dueLabel: string }>(
        `/api/homework/tasks?next=1&subject=${encodeURIComponent(subject.trim())}`,
      );
      if (next.dueDate) setDueDate(next.dueDate);
    } catch {
      // keep current date
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="gap-3 rounded-3xl p-4 sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{adding ? "Задати ДЗ" : "Завдання"}</DialogTitle>
          <DialogDescription>
            {adding && create
              ? `${create.slotOrder}. ${create.subject} · ${homeworkDueLabel(create.dueDate)}. Після збереження батькам піде коротке повідомлення.`
              : "Після збереження батькам піде лише те, що змінилось."}
          </DialogDescription>
        </DialogHeader>
        {adding ? null : (
          <Input
            className="h-11 rounded-2xl px-3"
            value={subject}
            placeholder="Предмет"
            onChange={(event) => setSubject(event.target.value)}
            onBlur={() => void onSubjectBlur()}
          />
        )}
        <Textarea
          className="min-h-24 rounded-2xl px-3"
          value={body}
          placeholder={adding ? "Текст завдання…" : undefined}
          autoFocus={adding}
          onChange={(event) => setBody(event.target.value)}
        />
        {adding ? null : (
          <label className="space-y-1 text-xs text-muted-foreground">
            Урок
            <Input
              type="date"
              className="mt-1 h-11 rounded-2xl px-3"
              value={dueDate}
              onChange={(event) => {
                setDueDate(event.target.value);
                setDueManual(true);
              }}
            />
          </label>
        )}
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" className="h-10 rounded-2xl" onClick={onClose} disabled={saving}>
            Скасувати
          </Button>
          <Button type="button" className="h-10 rounded-2xl" disabled={saving} onClick={() => void save()}>
            {saving ? "Зберігаю…" : "Зберегти"}
          </Button>
          {task ? (
            <Button
              type="button"
              variant="ghost"
              className="h-10 rounded-2xl text-destructive"
              disabled={deleting}
              onClick={() => void remove()}
            >
              Видалити
            </Button>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
